import { useConfirmationDialog } from '@dashhudson/dashing-ui/confirmation-dialog';
import { useDrawer } from '@dashhudson/dashing-ui/drawer';
import { useDashToast as useToast } from '@dashhudson/dashing-ui/toast';
import dayjs from 'dayjs';
import capitalize from 'lodash/capitalize';
import { defineStore } from 'pinia';
import { computed, defineAsyncComponent, reactive, ref } from 'vue';

import communityAPI, {
  getCommentInteractionDetail,
  saveCommunityInteractionTicket,
} from '@/apis/community';
import integrationApi from '@/apis/integration';
import { useCommunityPermissions } from '@/app/community/composables/useCommunityPermissions';
import {
  COMMUNITY_INTERACTION_PLATFORM_BY_TYPE,
  COMMUNITY_INTERACTION_TYPES,
} from '@/app/community/constants';
import { commentFormatter } from '@/app/community/utils/comments';
import { getInteractionUrl, getPlatformByType } from '@/app/community/utils/community-interactions';
import { communityInteractionProps } from '@/app/community/utils/mixpanel-events';
import { INTEGRATION_DRAWERS } from '@/app/settings/constants/integrations';
import { env } from '@/env';
import { INTEGRATION } from '@/models/integration.enum';
import { PLATFORM_CONNECTION_STATUS } from '@/models/platform/platform-connection.enum';
import { useAuthStore } from '@/stores/auth';
import { useCommunityStore } from '@/stores/community';
import { useCommunityCommentsStore } from '@/stores/community-comments';
import { useCommunityIntegrationsStore } from '@/stores/community-integrations';
import { useIdentityStore } from '@/stores/identity';
import { INTEGRATIONS, useIntegrationStore } from '@/stores/integration';
import { usePlatformStore } from '@/stores/platform';
import { useTrackingStore } from '@/stores/tracking';
import { logger } from '@/utils/logger';
import { normalizeMedia } from '@/utils/media';
import { toUnicodeVariant } from '@/vendor/toUnicodeVariant';

const WORKFLOWS = {
  [INTEGRATIONS.BRAND.SALESFORCE.value]: {
    CREATE_TICKET: '775247f2-ad6e-4391-99a5-5f5b64063d5b',
    GET_TICKET: 'e9c946e8-47d8-43b9-825c-a4947069f8ba',
  },
  [INTEGRATIONS.BRAND.ZENDESK.value]: {
    CREATE_TICKET: '638c5dd1-9f62-498a-95d4-f8faa0974c36',
    GET_TICKET: '3ec49b11-4d9f-4daa-9ba3-f7b9fc3dc436',
  },
};

const DEV_WORKFLOWS = {
  [INTEGRATIONS.BRAND.SALESFORCE.value]: {
    CREATE_TICKET: '775247f2-ad6e-4391-99a5-5f5b64063d5b',
    GET_TICKET: 'e9c946e8-47d8-43b9-825c-a4947069f8ba',
  },
  [INTEGRATIONS.BRAND.ZENDESK.value]: {
    CREATE_TICKET: '638c5dd1-9f62-498a-95d4-f8faa0974c36',
    GET_TICKET: '3ec49b11-4d9f-4daa-9ba3-f7b9fc3dc436',
  },
};

const TICKET_TOOLTIP_PREFIX = {
  [INTEGRATIONS.BRAND.SALESFORCE.value]: 'Case Number',
  [INTEGRATIONS.BRAND.ZENDESK.value]: 'Ticket #',
};

function getWorkflows(integrationType) {
  return env.isProduction ? WORKFLOWS[integrationType] : DEV_WORKFLOWS[integrationType];
}

export const useTicketStore = defineStore('ticket', () => {
  const integrationStore = useIntegrationStore();
  const authStore = useAuthStore();
  const identityStore = useIdentityStore();
  const communityStore = useCommunityStore();
  const commentsStore = useCommunityCommentsStore();
  const trackingStore = useTrackingStore();
  const platformStore = usePlatformStore();
  const communityIntegrationsStore = useCommunityIntegrationsStore();
  const { launchDrawer } = useDrawer();
  const { addToast } = useToast();

  const { canAccessSalesforceIntegration, brandHasAccessToSalesforceIntegration } =
    useCommunityPermissions();

  const salesforceReconnectBrandId = ref(null);
  const { showConfirmationDialog } = useConfirmationDialog({ group: 'global' });

  const createTicketPending = ref(false);
  const createTicketPopup = ref({
    show: false,
    integration: null,
    communityInteraction: null,
    message: null,
    comment: null,
  });
  const tickets = reactive({
    [INTEGRATIONS.BRAND.SALESFORCE.value]: {},
    [INTEGRATIONS.BRAND.ZENDESK.value]: {},
  });
  const ready = computed(() => integrationStore.authenticatedAsBrand);
  const isCreateTicketEnabled = computed(() => (integration) => {
    const integrationType = integrationStore.extractIntegrationType(integration);
    const workflowId = getWorkflows(integrationType)?.CREATE_TICKET;

    const integrationEnabled = integrationStore.isConnected(integrationType);
    const workflowEnabled = integrationStore.isWorkflowEnabled(integrationType, workflowId);

    return integrationEnabled && workflowEnabled;
  });

  const isSalesforceCreateTicketEnabled = computed(() =>
    isCreateTicketEnabled.value(INTEGRATIONS.BRAND.SALESFORCE),
  );
  const isZenDeskCreateTicketEnabled = computed(() =>
    isCreateTicketEnabled.value(INTEGRATIONS.BRAND.ZENDESK),
  );

  function formatEscalatedDate(date) {
    if (!date) {
      return '';
    }
    const timeZoneName = authStore.identity?.time_zone_name;
    const escalatedDate = timeZoneName ? dayjs(date).tz(timeZoneName) : dayjs(date).local();
    return escalatedDate.format('LLL');
  }

  function stringifyComment({ comment, communityInteraction }) {
    let escalatedText = '';
    const { platform } = getPlatformByType(communityInteraction);
    escalatedText += `${platform} User Name: ${comment.username}\n`;
    if (comment?.userProfile) {
      escalatedText += `User Profile Link: ${comment.userProfile}`;
    }
    escalatedText += `Text: ${comment?.text ?? ''}\n`;
    if (comment?.attachments?.length > 0 || comment?.attachment) {
      (comment.attachments ?? [comment.attachment]).forEach((attachment) => {
        escalatedText += `Attachment: ${attachment.src}\n`;
      });
    }
    escalatedText += `Date: ${formatEscalatedDate(comment.timestamp)}\n`;
    return escalatedText;
  }

  function stringifyMessage({ message, communityInteraction }) {
    let escalatedText = '';
    const { platform } = getPlatformByType(communityInteraction);
    const username = communityInteraction?.followerParticipant?.username;
    escalatedText += `${platform} User Name: ${username}\n`;
    escalatedText += `Text: ${message?.text ?? ''}\n`;
    if (message?.attachments?.length > 0 || message?.attachment) {
      (message.attachments ?? [message.attachment]).forEach((attachment) => {
        escalatedText += `Attachment: ${attachment.src}\n`;
      });
    }
    escalatedText += `Date: ${formatEscalatedDate(message?.updatedAt)}\n`;
    return escalatedText;
  }

  async function getFormattedCommentDetail(communityInteraction) {
    const commentDetail = await getCommentInteractionDetail({
      platform: COMMUNITY_INTERACTION_PLATFORM_BY_TYPE[communityInteraction?.type].platform,
      communityInteractionId: communityInteraction?.id,
    });
    const formattedCommentDetail = commentFormatter(commentDetail?.data, authStore.currentBrand);
    return formattedCommentDetail;
  }

  async function getEscalatedText({ communityInteraction, comment, message, includeThread }) {
    let escalatedText = '';
    let escalatedThread = '';
    let userHandle = '';
    let commentList = null;
    let messageList = null;
    const brandId = authStore.currentBrand?.id;
    const platform = communityInteraction?.type;
    const limit = 9999;

    if (comment) {
      escalatedText = stringifyComment({ comment, communityInteraction });
      userHandle = comment.username;
    } else if (message) {
      escalatedText = stringifyMessage({ message, communityInteraction });
      userHandle = communityInteraction?.followerParticipant?.username;
    }

    if (includeThread) {
      if (comment) {
        const mediaId = comment.mediaId;
        if (mediaId) {
          commentList = await communityStore.getCommentList({ brandId, platform, mediaId, limit });
          escalatedThread = (commentList ?? [])
            .map((c) => commentFormatter(c, authStore.currentBrand))
            .map((c) => stringifyComment({ comment: c, communityInteraction }))
            .join(`\n\n`);
        } else if (comment?.type === COMMUNITY_INTERACTION_TYPES.TWITTER_MENTION) {
          const formattedCommentDetail = await getFormattedCommentDetail(communityInteraction);
          escalatedThread = (formattedCommentDetail?.replies ?? [])
            .map((r) => stringifyComment({ comment: r, communityInteraction }))
            .join('\n\n');
        }
      } else if (message) {
        const communityInteractionId = communityInteraction.id;
        messageList = await communityStore.getMessages({ platform, communityInteractionId, limit });
        escalatedThread = (messageList ?? [])
          .map((m) => stringifyMessage({ message: m, communityInteraction }))
          .join(`\n\n`);
      } else {
        // clicked from interaction overview of a tweet
        // topParentStatus is available when a selected interaction is reply to ugc mention
        const target = communityInteraction.topParentStatus ?? communityInteraction;
        const formattedCommentDetail = await getFormattedCommentDetail(target);
        userHandle = formattedCommentDetail.username;
        escalatedText = stringifyComment({
          comment: formattedCommentDetail,
          communityInteraction: formattedCommentDetail,
        });
        escalatedThread = (formattedCommentDetail?.replies ?? [])
          .map((r) => stringifyComment({ comment: r, communityInteraction: target }))
          .join('\n\n');
      }
    } else if (!comment && !message && communityInteraction) {
      // clicked from interaction overview but didn't include thread
      const target = communityInteraction.topParentStatus ?? communityInteraction;
      const formattedTarget = commentFormatter(target, authStore.currentBrand);
      escalatedText = stringifyComment({
        comment: formattedTarget,
        communityInteraction: formattedTarget,
      });
      userHandle = formattedTarget.username;
    }
    return {
      escalatedText,
      escalatedThread,
      userHandle,
    };
  }

  function openCreateTicketPopup(
    integration,
    { communityInteraction, communityInteractionMedia, message, comment } = {},
  ) {
    const integrationMixpanelProps = integrationStore.integrationMixpanelProps(integration);
    trackingStore.track('Send to Integration Selected', {
      ...integrationMixpanelProps,
      ...communityInteractionProps(communityInteraction),
    });
    createTicketPopup.value.integration = integration;
    createTicketPopup.value.communityInteraction = communityInteraction;
    createTicketPopup.value.communityInteractionMedia = communityInteractionMedia;
    createTicketPopup.value.message = message;
    createTicketPopup.value.comment = comment;
    createTicketPopup.value.show = true;
  }

  function closeCreateTicketPopup() {
    createTicketPopup.value.integration = null;
    createTicketPopup.value.communityInteraction = null;
    createTicketPopup.value.communityInteractionMedia = null;
    createTicketPopup.value.message = null;
    createTicketPopup.value.comment = null;
    createTicketPopup.value.show = false;
  }

  async function getTicketFromSource({ integration, sourceId }, brandId) {
    const integrationType = integrationStore.extractIntegrationType(integration);

    if (
      integrationType === INTEGRATIONS.BRAND.SALESFORCE.value &&
      canAccessSalesforceIntegration.value
    ) {
      try {
        const res = await integrationApi.getSalesforceCase(brandId, sourceId);

        const ticketData = res.data?.data?.[sourceId];

        if (ticketData && ticketData.id) {
          tickets[integrationType][`${ticketData.id}`] = ticketData;
        }

        return ticketData;
      } catch (error) {
        logger.error(`Failed to get Salesforce case from integration backend: ${error}`);
        throw error;
      }
    }

    const workflowId = getWorkflows(integrationType)?.GET_TICKET;
    if (!workflowId) {
      logger.error(`No 'Get Ticket' workflow for ${integrationType}`);
      return null;
    }
    const response = await integrationStore.runWorkflow(workflowId, {
      body: {
        source_id: `${sourceId}`,
      },
    });

    if (response?.ticket && response?.source_id) {
      tickets[integrationType][`${response.source_id}`] = response.ticket;
    }

    return response;
  }

  async function updateTicket(communityInteraction, ticket) {
    if (!communityInteraction || !ticket) {
      return null;
    }

    const ticketFromSource = await getTicketFromSource(
      {
        integration: ticket?.source,
        sourceId: `${ticket?.sourceId}`,
      },
      communityInteraction.brandId,
    );

    let updatedTicket = { ...ticket };

    const sourceUpdatedAt =
      ticketFromSource?.sourceUpdatedAt ?? ticketFromSource?.source_updated_at;
    const sourceCreatedAt =
      ticketFromSource?.sourceCreatedAt ?? ticketFromSource?.source_created_at;

    const isNew = !!ticket?.isNew;
    const hasChanged =
      ticketFromSource?.status !== ticket?.status ||
      `${ticketFromSource?.label}` !== ticket?.label ||
      sourceUpdatedAt !== ticket?.sourceUpdatedAt;
    if (isNew || hasChanged) {
      const response = await saveCommunityInteractionTicket({
        communityInteractionId: communityInteraction?.id,
        messageId: ticket?.messageId,
        source: ticket?.source,
        sourceId: `${ticket.sourceId}`,
        label: `${ticketFromSource?.label ?? ticket.label}`,
        sourceCreatedAt: sourceCreatedAt ?? ticket?.sourceCreatedAt,
        sourceUpdatedAt: sourceUpdatedAt ?? ticket?.sourceUpdatedAt,
        status: ticketFromSource?.status ?? ticket?.status,
      });
      updatedTicket = response?.data;
    }
    return updatedTicket;
  }

  async function ticketUrl({ integration, id }) {
    const integrationType = integrationStore.extractIntegrationType(integration);

    if (integrationType === INTEGRATIONS.BRAND.SALESFORCE.value) {
      let instanceUrl;

      if (canAccessSalesforceIntegration.value) {
        const brandId = communityIntegrationsStore.selectedBrandId;
        const salesforceAccountForBrand = communityIntegrationsStore.salesforceAccounts.find(
          (account) => account.brandId === brandId,
        );
        instanceUrl = salesforceAccountForBrand?.instanceUrl;
      } else {
        instanceUrl = await integrationStore.instanceUrl(integration);
      }

      return instanceUrl ? `${instanceUrl}/lightning/r/Case/${id}/view` : null;
    }

    if (integrationType === INTEGRATIONS.BRAND.ZENDESK.value) {
      /*
          This is a temp solution until paragon puts some sort of instance/subdomain
          data in the integration.providerData json for zendesk.
        */
      const zendeskTicketUrl = tickets[integrationType]?.[id]?.url;
      if (zendeskTicketUrl) {
        const url = new URL(zendeskTicketUrl);
        return `${url.origin}/agent/tickets/${id}`;
      }
    }
    return null;
  }

  async function updateTickets({ communityInteraction }) {
    if (communityInteraction?.tickets?.length < 1) {
      return;
    }

    const savedTickets = await Promise.all(
      communityInteraction?.tickets?.map(async (ticket) =>
        updateTicket(communityInteraction, ticket),
      ) ?? [],
    );

    savedTickets.forEach((savedTicket) => {
      const index =
        communityInteraction?.tickets?.findIndex(
          (ticket) =>
            ticket?.source === savedTicket?.source &&
            `${ticket?.sourceId}` === `${savedTicket?.sourceId}`,
        ) ?? -1;
      if (index > -1) {
        communityInteraction.tickets.splice(index, 1, savedTicket);
        communityInteraction.tickets = [...communityInteraction.tickets];
      } else {
        communityInteraction.tickets = [...(communityInteraction.tickets ?? []), savedTicket];
      }
    });
  }

  async function createSalesforceTicket(
    interaction,
    integrationType,
    { customSubject, customDescription } = {},
    communityMessageText = null,
  ) {
    const mapping = communityIntegrationsStore.salesforceMapping;

    if (!mapping?.mappings?.recordType?.id) {
      throw new Error('Salesforce mapping not available or missing record type ID');
    }

    const casePayload = {
      brandId: interaction.brandId,
      recordTypeId: mapping.mappings.recordType.id,
      integrationType,
      communityInteractionId: interaction?.id,
    };
    if (customSubject?.length > 0) {
      casePayload.customSubject = customSubject;
    }
    if (customDescription?.length > 0) {
      casePayload.customDescription = customDescription;
    }
    if (communityMessageText) {
      casePayload.communityMessageText = communityMessageText;
    }

    let response;
    try {
      response = await communityAPI.createSalesforceCase(casePayload);
    } catch (error) {
      const description = error?.response?.data?.description || '';
      if (
        error?.response?.status === 500 ||
        description.includes('Failed to connect to Salesforce API')
      ) {
        if (identityStore.checkIfUserIsAdminForBrand(interaction.brandId)) {
          salesforceReconnectBrandId.value = interaction.brandId;
          const confirmed = await showConfirmationDialog({
            group: 'global',
            header: 'Reconnect Salesforce',
            message:
              'Your Salesforce permissions have changed. Please reconnect to continue using Dash Social with Salesforce.',
            acceptLabel: 'Reconnect Salesforce',
            acceptSeverity: 'primary',
          });
          if (confirmed) {
            await platformStore.connectPlatform({
              router: null,
              provider: INTEGRATIONS.BRAND.SALESFORCE.value,
              connectionId:
                platformStore.getPlatformConnection(INTEGRATION.SALESFORCE, {
                  brandId: salesforceReconnectBrandId.value,
                })?.id ?? null,
              brandId: salesforceReconnectBrandId.value,
              invitedByBrandId: null,
              invitedByBrandName: null,
              influencer: null,
              fromExternalLink: false,
              sourceCreatorId: null,
            });
          }
        } else {
          addToast({
            severity: 'error',
            summary: 'Reconnect Salesforce',
            message:
              'Your Salesforce permissions have changed. Please contact your admin to reconnect Salesforce.',
          });
        }
      }
      throw error;
    }

    return {
      source_id: response.data.sourceId,
      status: response.data.status,
      source_created_at: response.data.sourceCreatedAt,
      source_updated_at: response.data.sourceUpdatedAt,
    };
  }

  async function createWorkflowTicket(
    targetInteraction,
    { subject, description, includeThread },
    integrationType,
  ) {
    const requesterName = `${authStore.identity.first_name} ${authStore.identity.last_name}`;
    const requesterEmail = authStore.identity.email;
    const communityInteractionNormalizedMedia = normalizeMedia(
      createTicketPopup.value.communityInteractionMedia,
    );
    const message = createTicketPopup.value.message;
    const comment = createTicketPopup.value.comment;

    const interactionUrl = getInteractionUrl(targetInteraction);
    const { escalatedText, escalatedThread, userHandle } = await getEscalatedText({
      communityInteraction: targetInteraction,
      message,
      comment,
      includeThread,
    });

    let content = '';
    content += `${toUnicodeVariant(`Requester`, 'b')}\n`;
    content += ` ${requesterName} (${requesterEmail})\n\n`;
    content += `${toUnicodeVariant(`Description`, 'b')}\n`;
    content += ` ${description}\n\n`;
    content += `${toUnicodeVariant(`Escalated Text`, 'b')}\n`;
    content += ` ${escalatedText}\n\n`;
    if (communityInteractionNormalizedMedia?.postUrl) {
      content += `${toUnicodeVariant(`Post Link`, 'b')}\n`;
      content += `${communityInteractionNormalizedMedia.postUrl}\n\n`;
    }
    content += `${toUnicodeVariant(`Dash Social Link`, 'b')}\n`;
    content += `${interactionUrl}\n\n`;
    if (escalatedThread) {
      content += `${toUnicodeVariant(`Escalated Thread History`, 'b')}\n`;
      content += ` ${escalatedThread}\n\n`;
    }

    const workflowId = getWorkflows(integrationType)?.CREATE_TICKET;
    return integrationStore.runWorkflow(workflowId, {
      body: {
        subject,
        description: content,
        requesterName,
        requesterEmail,
        userHandle,
      },
    });
  }

  function buildNewTicket(payload, integrationType, messageIdForTracking) {
    return {
      isNew: true,
      messageId: messageIdForTracking,
      source: integrationType?.toUpperCase(),
      sourceId: `${payload.source_id}`,
      label: payload?.label,
      sourceCreatedAt: payload?.source_created_at,
      sourceUpdatedAt: payload?.source_updated_at,
      status: payload?.status,
    };
  }

  function trackTicketCreationResult(
    success,
    integrationMixpanelProps,
    targetInteraction,
    messageIdForTracking,
    description,
    includeThread,
    error = null,
  ) {
    const eventData = {
      ...integrationMixpanelProps,
      ...communityInteractionProps(targetInteraction),
      messageIdForTracking,
      messageDescriptionUsed: !!description,
      threadIncluded: includeThread,
    };

    if (success) {
      trackingStore.track('Sent to Integration', eventData);
    } else {
      trackingStore.track('Send to Integration Failure', {
        ...eventData,
        errorType: error?.message ?? error,
      });
    }
  }

  async function createTicket(
    integration,
    { subject, description, includeThread, customSubject, customDescription } = {},
    interaction = {},
    communityMessageId = null,
    communityMessageText = null,
  ) {
    const integrationType = integrationStore.extractIntegrationType(integration);
    const integrationMixpanelProps = integrationStore.integrationMixpanelProps(integration);
    const hasSalesforceFFEnabled =
      integrationType === INTEGRATIONS.BRAND.SALESFORCE.value &&
      canAccessSalesforceIntegration.value;

    // Determine which interaction and messageId to use based on FF status
    const targetInteraction = hasSalesforceFFEnabled
      ? interaction
      : createTicketPopup.value.communityInteraction;
    const messageIdForTracking = hasSalesforceFFEnabled
      ? (communityMessageId ?? interaction.id)
      : (createTicketPopup.value.message?.id ?? createTicketPopup.value.comment?.id);

    let payload = null;

    try {
      if (hasSalesforceFFEnabled) {
        payload = await createSalesforceTicket(
          targetInteraction,
          integrationType,
          { customSubject, customDescription },
          communityMessageText,
        );
      } else {
        payload = await createWorkflowTicket(
          targetInteraction,
          { subject, description, includeThread },
          integrationType,
        );
      }
    } catch (error) {
      trackTicketCreationResult(
        false,
        integrationMixpanelProps,
        targetInteraction,
        messageIdForTracking,
        description,
        includeThread,
        error,
      );
      throw error;
    }

    trackTicketCreationResult(
      true,
      integrationMixpanelProps,
      targetInteraction,
      messageIdForTracking,
      description,
      includeThread,
    );

    const newTicket = buildNewTicket(payload, integrationType, messageIdForTracking);
    targetInteraction.tickets = [...(targetInteraction.tickets ?? []), newTicket];
    await updateTickets({ communityInteraction: targetInteraction });

    if (commentsStore.commentDetail?.id === targetInteraction.id) {
      commentsStore.commentDetail = {
        ...commentsStore.commentDetail,
        tickets: targetInteraction.tickets,
      };
    }
  }

  function getTicketDropdownOptions({
    communityInteraction,
    communityInteractionMedia,
    message,
    comment,
  } = {}) {
    const options = [];
    if (integrationStore.hasAccessToIntegrations && integrationStore.authenticatedAsBrand) {
      if (isSalesforceCreateTicketEnabled.value) {
        options.push({
          text: 'Send to Salesforce',
          action: () => {
            openCreateTicketPopup(INTEGRATIONS.BRAND.SALESFORCE, {
              communityInteraction,
              communityInteractionMedia,
              message,
              comment,
            });
          },
        });
      } else if (identityStore.checkIfUserIsAdminForBrand(communityInteraction.brandId)) {
        options.push({
          text: 'Connect to Salesforce',
          optionImage: integrationStore.integrationIcon(INTEGRATIONS.BRAND.SALESFORCE),
          action: () => {
            integrationStore.connect(INTEGRATIONS.BRAND.SALESFORCE);
          },
        });
      }
      if (isZenDeskCreateTicketEnabled.value) {
        options.push({
          text: 'Send to Zendesk',
          action: () => {
            openCreateTicketPopup(INTEGRATIONS.BRAND.ZENDESK, {
              communityInteraction,
              communityInteractionMedia,
              message,
              comment,
            });
          },
        });
      } else if (identityStore.checkIfUserIsAdminForBrand(communityInteraction.brandId)) {
        options.push({
          text: 'Connect to Zendesk',
          optionImage: integrationStore.integrationIcon(INTEGRATIONS.BRAND.ZENDESK),
          action: () => {
            integrationStore.connect(INTEGRATIONS.BRAND.ZENDESK);
          },
        });
      }
    }
    return options;
  }

  async function sendTicket(integrationType, payload) {
    createTicketPending.value = true;
    try {
      await createTicket(integrationType, payload);
    } finally {
      createTicketPending.value = false;
      closeCreateTicketPopup();
    }
  }

  function getTicketMenuItems({ interaction, interactionMedia, message, comment } = {}) {
    const options = [];
    if (integrationStore.hasAccessToIntegrations && integrationStore.authenticatedAsBrand) {
      if (brandHasAccessToSalesforceIntegration(interaction.brandId)) {
        const brandId = interaction.brandId;
        const salesforceConnection = platformStore.platformConnectionsMap?.salesforce?.[brandId];
        const isConnected = salesforceConnection?.status === PLATFORM_CONNECTION_STATUS.CONNECTED;

        // The Salesforce integration drawer UI will change based on the connection status
        if (isConnected) {
          options.push({
            icon: 'salesforce',
            label: 'Create Salesforce Case',
            form: defineAsyncComponent(
              () => import('@/app/community/components/ticket/CreateSalesforceCaseForm.vue'),
            ),
            action: 'send',
            integrationName: integrationStore.integrationName('salesforce'),
            command: async (payload = {}) => {
              await communityIntegrationsStore.getSalesforceMapping(brandId);
              const hasMapping =
                communityIntegrationsStore.salesforceMapping?.mappings?.recordType?.id;
              if (!hasMapping || communityIntegrationsStore.salesforceMappingHasErrors()) {
                if (!identityStore.checkIfUserIsAdminForBrand(brandId)) {
                  addToast({
                    severity: 'error',
                    summary: 'Salesforce Field Mapping Setup Required',
                    message:
                      "Field mapping isn't set up for this brand yet. Ask your admin to set it up so you can create Salesforce cases.",
                  });
                  throw new Error('Mapping not configured');
                }
                const confirmed = await showConfirmationDialog({
                  group: 'global',
                  header: 'Salesforce Field Mapping Setup Required',
                  message:
                    'To create cases in Salesforce, map your Dash Social fields to Salesforce fields.',
                  acceptLabel: 'Map Fields',
                  acceptSeverity: 'primary',
                });
                if (confirmed) {
                  await launchDrawer({
                    name: INTEGRATION_DRAWERS.SALESFORCE_INTEGRATION,
                    props: { selectedBrandId: interaction.brandId },
                  });
                }
                throw new Error('Mapping not configured');
              }
              await createTicket(
                'SALESFORCE',
                payload,
                interaction,
                message?.id ?? comment?.id,
                message?.text ?? comment?.text,
              );
            },
          });
        } else if (identityStore.checkIfUserIsAdminForBrand(brandId)) {
          options.push({
            icon: 'salesforce',
            label: 'Connect to Salesforce',
            action: 'connect',
            command: () => {
              launchDrawer({
                name: INTEGRATION_DRAWERS.SALESFORCE_INTEGRATION,
                props: { selectedBrandId: interaction.brandId },
              });
            },
          });
        }
      } else if (isSalesforceCreateTicketEnabled.value) {
        options.push({
          icon: 'salesforce',
          label: 'Send to Salesforce',
          form: defineAsyncComponent(
            () => import('@/app/community/components/CreateTicketForm.vue'),
          ),
          formProps: {
            variant: 'salesforce',
          },
          action: 'send',
          integrationName: integrationStore.integrationName('salesforce'),
          command: async (payload) => {
            createTicketPopup.value = {
              integration: 'salesforce',
              communityInteraction: interaction,
              communityInteractionMedia: interactionMedia,
              message,
              comment,
            };
            await sendTicket('salesforce', payload);
          },
        });
      } else if (identityStore.checkIfUserIsAdminForBrand(interaction.brandId)) {
        options.push({
          icon: 'salesforce',
          label: 'Connect to Salesforce',
          action: 'connect',
          command: () => {
            integrationStore.connect(INTEGRATIONS.BRAND.SALESFORCE);
          },
        });
      }
      if (isZenDeskCreateTicketEnabled.value) {
        options.push({
          icon: 'zendesk',
          label: 'Send to Zendesk',
          form: defineAsyncComponent(
            () => import('@/app/community/components/CreateTicketForm.vue'),
          ),
          formProps: {
            variant: 'zendesk',
          },
          action: 'send',
          integrationName: integrationStore.integrationName('zendesk'),
          command: async (payload) => {
            createTicketPopup.value = {
              integration: 'zendesk',
              communityInteraction: interaction,
              communityInteractionMedia: interactionMedia,
              message,
              comment,
            };
            await sendTicket('zendesk', payload);
          },
        });
      } else if (identityStore.checkIfUserIsAdminForBrand(interaction.brandId)) {
        options.push({
          icon: 'zendesk',
          label: 'Connect to Zendesk',
          action: 'connect',
          command: () => {
            integrationStore.connect(INTEGRATIONS.BRAND.ZENDESK);
          },
        });
      }
    }
    return options;
  }

  async function getTicketChips(payload) {
    if (integrationStore.hasAccessToIntegrations) {
      const sortedTickets = (payload?.tickets ?? []).sort(
        (a, b) => new Date(a.sourceCreatedAt) - new Date(b.sourceCreatedAt),
      );

      const ticketChipsPromises = sortedTickets.map(async (ticket) => {
        const integrationType = integrationStore.extractIntegrationType(ticket.source);
        const ticketHref = await ticketUrl({
          integration: ticket.source,
          id: `${ticket?.sourceId}`,
        });
        const sourceName = integrationStore.integrationName(ticket.source) ?? ticket.source;
        const sourceIcon = integrationStore.integrationIcon(ticket.source);
        const ticketStatus = capitalize(ticket.status);
        const ticketLabel = ticket.label;
        const tooltipPrefix = TICKET_TOOLTIP_PREFIX[integrationType] ?? '';
        const redirectMessage = ticketHref
          ? `. Click the icon to be redirected to ${sourceName}.`
          : '';
        const ticketTooltip = `${tooltipPrefix} ${ticketLabel} is ${ticketStatus}${redirectMessage}`;
        return {
          ...ticket,
          sourceName,
          sourceIcon,
          ticketHref,
          ticketTooltip,
          goToTicket: () => {
            if (ticketHref) {
              const integrationMixpanelProps =
                integrationStore.integrationMixpanelProps(integrationType);
              trackingStore.track('View Ticket Selected', {
                ...integrationMixpanelProps,
                ...communityInteractionProps(payload?.communityInteraction),
                messageId: ticket?.messageId,
                ticketStatus,
                ticketLabel,
                ticketHref,
              });
              window.open(ticketHref, '_blank');
            }
          },
        };
      });

      return Promise.all(ticketChipsPromises);
    }
    return [];
  }

  return {
    ready,
    createTicketPopup,
    openCreateTicketPopup,
    closeCreateTicketPopup,
    isSalesforceCreateTicketEnabled,
    isZenDeskCreateTicketEnabled,
    isCreateTicketEnabled,
    getTicketDropdownOptions,
    getTicketMenuItems,
    getTicketChips,
    createTicket,
    ticketUrl,
    updateTickets,
  };
});
